/**
 * API client para o subsistema `core/importacoes/` do backend.
 *
 * Endpoints (sem trailing slash):
 *   GET  /api/importacoes/tipos
 *   GET  /api/importacoes/historico
 *   POST /api/importacoes/{tipo-slug}?dry_run=true|false
 *
 * Tipo na URL usa hyphen (`clientes-gerenciador`); internamente o backend
 * converte para underscore (`clientes_gerenciador`).
 */

import { ACCESS_API_URL } from "../config/api";

const PREFIXO = "/api/importacoes";

export function listarTipos(request) {
  return request(`${PREFIXO}/tipos`);
}

export function listarHistorico(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.tipo) usp.set("tipo", params.tipo);
  if (params.page) usp.set("page", params.page);
  if (params.page_size) usp.set("page_size", params.page_size);
  const qs = usp.toString();
  return request(`${PREFIXO}/historico${qs ? `?${qs}` : ""}`);
}

/**
 * Executa uma importacao (dry-run ou apply).
 *
 * @param {Function} request — vem de useAuth().request
 * @param {string} tipoSlug — ex.: "clientes-gerenciador"
 * @param {object} options
 * @param {File} options.arquivo — arquivo .xlsx/.xlsm
 * @param {boolean} options.dryRun — se true, preview; se false, persiste
 * @param {boolean} options.incluirDetalhes — se true, resposta lista linha-a-linha
 * @returns {Promise<object>} ResultadoImportacao serializado
 */
export function executarImportacao(
  request,
  tipoSlug,
  { arquivo, dryRun = false, incluirDetalhes = false, opcoes = {}, campos = {} },
) {
  const usp = new URLSearchParams();
  if (dryRun) usp.set("dry_run", "true");
  if (incluirDetalhes) usp.set("incluir_detalhes", "true");
  // Opcoes escolhidas na UI (ex.: etapa_id para leads) viram query params.
  Object.entries(opcoes || {}).forEach(([chave, valor]) => {
    if (valor !== undefined && valor !== null && valor !== "") {
      usp.set(chave, valor);
    }
  });
  const qs = usp.toString();

  const formData = new FormData();
  formData.append("arquivo", arquivo);
  // Opcoes grandes (ex.: mapeamento JSON material -> produto) vao no corpo,
  // nao na URL: a view le `request.data` para o que nao veio na query.
  Object.entries(campos || {}).forEach(([chave, valor]) => {
    if (valor !== undefined && valor !== null && valor !== "") formData.append(chave, valor);
  });

  return request(`${PREFIXO}/${tipoSlug}${qs ? `?${qs}` : ""}`, {
    method: "POST",
    body: formData,
  });
}

/** Converte o tipo interno (`clientes_gerenciador`) para o slug de URL. */
export function tipoToSlug(tipo) {
  return String(tipo || "").replace(/_/g, "-");
}

/**
 * Baixa o XLSX modelo de um tipo (R20). O `request()` do AuthContext faz JSON
 * parse, entao para receber binario usamos fetch direto com o accessToken do
 * localStorage — mesmo pattern de `services/producao.js` (etiquetas PDF).
 *
 * @param {string} tipoSlug — ex.: "fornecedores"
 */
export async function baixarModelo(tipoSlug) {
  const apiUrl = ACCESS_API_URL;
  const session = JSON.parse(
    window.localStorage.getItem("pharuerp:access-session") || "{}",
  );
  const accessToken = session?.accessToken;
  const headers = {};
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const resp = await fetch(`${apiUrl}${PREFIXO}/${tipoSlug}/template`, {
    method: "GET",
    headers,
  });
  if (!resp.ok) {
    let detalhe = `Erro ${resp.status} ao baixar o modelo.`;
    try {
      const data = await resp.json();
      detalhe = data?.detalhe || data?.detail || detalhe;
    } catch {
      /* body nao eh json */
    }
    const err = new Error(detalhe);
    err.status = resp.status;
    throw err;
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `modelo_${String(tipoSlug).replace(/-/g, "_")}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return { ok: true };
}
